import { Box, Container, makeStyles } from '@material-ui/core';

import {
  HeroSlide,
  Category,
  Feedback,
  PageMeta,
  ProductPreview,
  ProductSetPreview,
  FeaturedProductPreviewWithColorCollection
} from 'domain/types';
import {
  CategoriesRepo,
  ProductPreviewsRepo,
  HeroSlidesRepo,
  PageMetaRepo,
  SetsRepo
} from 'domain/repositories';
import { FeedbacksRepo } from 'domain/repositories/FeedbacksRepo';
import FeedbacksPreview from 'components/home/FeedbacksPreview/FeedbacksPreview';
import HeroSlider from 'components/home/HeroSlider/HeroSlider';
import Layout from 'components/layout/Layout';
import FeaturedProducts from 'components/product/FeaturedProducts/FeaturedProducts';
import CategoryTiles from 'components/home/CategoryTiles/CategoryTiles';
import CategoryNavigation from 'components/shop/CategoryNavigation/CategoryNavigation';
import HomeBlock from 'components/home/HomeBlock';
import Features from 'components/home/Features/Features';
import JsonLd from 'components/layout/JsonLd';
import { featuredProductsToMainProductPreviews } from 'utils/helpers/products/featuredProductsToMainProductPreviews';
import { setsToProductPreviews } from 'utils/helpers/sets/setsToProductPreviews';
import { APP_URL, DEFAULT_META_DATA, REVALIDATES } from 'domain/constants';
import { CONTACTS } from 'domain/constants/CONTACTS';

interface HomeProps {
  heroSlides: Array<HeroSlide>;
  featuredProducts: ProductPreview[];
  bestsellers: ProductPreview[];
  categories: Array<Category>;
  feedbacks: Feedback[];
  pageMeta: PageMeta;
}

const Home: React.FC<HomeProps> = ({
  heroSlides,
  featuredProducts,
  bestsellers,
  categories,
  feedbacks,
  pageMeta
}): JSX.Element => {
  const classes = useStyles();

  return (
    <Layout pageMeta={pageMeta}>
      <JsonLd
        keyName="organization-jsonld"
        data={{
          '@context': 'https://schema.org',
          '@type': 'OnlineStore',
          name: DEFAULT_META_DATA.title,
          description: DEFAULT_META_DATA.description,
          url: APP_URL,
          logo: `${APP_URL}/logo.png`,
          email: CONTACTS.email,
          telephone: CONTACTS.phoneNumber,
          sameAs: [CONTACTS.instagramUrl]
        }}
      />
      <HeroSlider heroSlides={heroSlides} />

      <Box className={classes.categoryNavigation}>
        <CategoryNavigation categories={categories} />
      </Box>

      <Container>
        <HomeBlock title="Новинки" dense tightBottom={bestsellers.length > 0}>
          <FeaturedProducts products={featuredProducts} size="small" />
        </HomeBlock>

        {bestsellers.length > 0 && (
          <HomeBlock title="Хіти продажів" dense>
            <FeaturedProducts products={bestsellers} size="small" />
          </HomeBlock>
        )}

        <HomeBlock title="Магазин">
          <CategoryTiles categories={categories} />
        </HomeBlock>

        <Features />

        {feedbacks.length > 0 && (
          <HomeBlock title="Відгуки" dense>
            <FeedbacksPreview feedbacks={feedbacks} />
          </HomeBlock>
        )}
      </Container>
    </Layout>
  );
};

const useStyles = makeStyles((theme) => ({
  categoryNavigation: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block'
    }
  }
}));

export const getStaticProps = async () => {
  const [
    heroSlides,
    featuredProductsWithColorCollection,
    bestsellersWithColorCollection,
    bestsellerSets,
    categories,
    feedbacks,
    pageMeta
  ] = await Promise.all([
    HeroSlidesRepo.get(),
    ProductPreviewsRepo.getFeaturedProductsWithColors(),
    ProductPreviewsRepo.getBestsellersWithColors().catch(
      () => [] as FeaturedProductPreviewWithColorCollection[]
    ),
    SetsRepo.getBestsellerPreviews().catch(() => [] as ProductSetPreview[]),
    CategoriesRepo.get(),
    FeedbacksRepo.getAll().catch(() => [] as Feedback[]),
    PageMetaRepo.getBySlug('index')
  ]);

  const featuredProducts = featuredProductsToMainProductPreviews(
    featuredProductsWithColorCollection
  ).filter(({ available }) => available > 0);

  const bestsellers = [
    ...featuredProductsToMainProductPreviews(bestsellersWithColorCollection),
    ...setsToProductPreviews(bestsellerSets)
  ].filter(({ available }) => available > 0);

  return {
    props: {
      heroSlides,
      featuredProducts,
      bestsellers,
      categories,
      feedbacks: feedbacks.slice(0, 8),
      pageMeta
    },
    revalidate: REVALIDATES.index
  };
};

export default Home;
